import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Settings, ListChecks, GripVertical, ArrowUpDown, Search } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResultOptions = () => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [newOptionName, setNewOptionName] = useState("");
  const [generatesCredit, setGeneratesCredit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // New states for sorting and filtering
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState(""); // "name", "type", "created"
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchOptions();
  }, []);
  
  // Filter and sort options
  const filteredAndSortedOptions = () => {
    let filtered = [...options];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(opt => 
        opt.name.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    if (sortKey) {
      filtered.sort((a, b) => {
        let aVal, bVal;
        
        if (sortKey === "name") {
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
        } else if (sortKey === "type") {
          aVal = a.generates_credit ? 1 : 0;
          bVal = b.generates_credit ? 1 : 0;
        } else if (sortKey === "created") {
          aVal = a.created_at || "";
          bVal = b.created_at || "";
        }
        
        if (sortDirection === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }
    
    return filtered;
  };
  
  const handleSort = (key) => {
    if (sortKey === key) {
      // Toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };
  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setOptions((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      
      toast.success("Order updated");
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/result-options`);
      setOptions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch options:", error);
      toast.error("Failed to load result options");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newOptionName.trim()) {
      toast.error("Please enter an option name");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/admin/result-options`, {
        name: newOptionName.trim(),
        generates_credit: generatesCredit
      });
      setOptions([...options, response.data]);
      setNewOptionName("");
      setGeneratesCredit(false);
      setDialogOpen(false);
      toast.success("Option created successfully");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create option";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (option) => {
    setEditingOption(option);
    setNewOptionName(option.name);
    setGeneratesCredit(option.generates_credit || false);
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!newOptionName.trim()) {
      toast.error("Please enter an option name");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.put(`${API}/admin/result-options/${editingOption.id}`, {
        name: newOptionName.trim(),
        generates_credit: generatesCredit
      });
      setOptions(options.map(opt => opt.id === editingOption.id ? response.data : opt));
      setNewOptionName("");
      setGeneratesCredit(false);
      setEditingOption(null);
      setEditDialogOpen(false);
      toast.success("Option updated successfully");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update option";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (optionId, optionName) => {
    if (!window.confirm(`Are you sure you want to delete "${optionName}"? This will not affect existing call results.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/result-options/${optionId}`);
      setOptions(options.filter(o => o.id !== optionId));
      toast.success("Option deleted");
    } catch (error) {
      toast.error("Failed to delete option");
    }
  };

  // Sortable Row Component
  const SortableRow = ({ option, onEdit, onDelete }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: option.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <TableRow 
        ref={setNodeRef} 
        style={style} 
        className="hover:bg-[#0d2a5a] border-b border-[#1a3a6a]"
      >
        <TableCell className="w-8">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-white/40 hover:text-white/70">
            <GripVertical size={18} />
          </div>
        </TableCell>
        <TableCell className="font-medium text-white">
          {option.name}
        </TableCell>
        <TableCell>
          {option.generates_credit ? (
            <Badge className="bg-red-600/90 text-white border-red-500">
              Credit
            </Badge>
          ) : (
            <Badge className="bg-green-600/90 text-white border-green-500">
              Qualified
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-white/60 text-sm">
          {option.created_at ? new Date(option.created_at).toLocaleDateString() : "-"}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(option)}
              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
            >
              <Edit2 size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(option.id, option.name)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
              data-testid={`delete-option-${option.id}`}
            >
              <Trash2 size={16} />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-full bg-[#f4cc1e] p-4 sm:p-6 rounded-xl min-h-screen" data-testid="result-options-page">
      {/* Header */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white font-['Chivo']">Disposition Values</h1>
              <p className="text-sm text-white/60">Manage dropdown options for call results in Customer view</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => setNewOptionName("")}
                  className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold uppercase tracking-wide rounded-xl w-full sm:w-auto"
                >
                  <Plus size={18} className="mr-2" />
                  Add Option
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white">
                <DialogHeader>
                  <DialogTitle className="font-['Chivo'] text-white">
                    Add New Result Option
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="option_name" className="text-white/80">Option Name *</Label>
                    <Input
                      id="option_name"
                      value={newOptionName}
                      onChange={(e) => setNewOptionName(e.target.value)}
                      placeholder="e.g., Inspected, Claim Filed, Not Qualified"
                      className="bg-[#0d2a5a] border-[#1a3a6a] text-white"
                      data-testid="option-name-input"
                    />
                    <p className="text-xs text-white/50">
                      This will appear in the Results dropdown on the Customer Call History table
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2 py-2">
                    <Checkbox
                      id="generates_credit"
                      checked={generatesCredit}
                      onCheckedChange={setGeneratesCredit}
                      className="border-white/30 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                    />
                    <div className="flex flex-col">
                      <Label 
                        htmlFor="generates_credit" 
                        className="text-white/90 cursor-pointer font-medium"
                      >
                        Generates Credit
                      </Label>
                      <p className="text-xs text-white/50">
                        Mark this if selecting this result should mark the call as a credit (not payable)
                      </p>
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setDialogOpen(false)}
                      className="text-white hover:bg-[#1a3a6a] border border-[#1a3a6a]"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
                      data-testid="save-option-btn"
                    >
                      {submitting ? "Creating..." : "Create Option"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-white font-['Chivo']">Edit Result Option</DialogTitle>
            <DialogDescription className="text-white/60">
              Update the disposition name or credit settings
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_option_name" className="text-white/80">Option Name *</Label>
              <Input
                id="edit_option_name"
                value={newOptionName}
                onChange={(e) => setNewOptionName(e.target.value)}
                placeholder="e.g., Inspected, Claim Filed, Not Qualified"
                className="bg-[#0d2a5a] border-[#1a3a6a] text-white"
              />
            </div>
            
            <div className="flex items-center space-x-2 py-2">
              <Checkbox
                id="edit_generates_credit"
                checked={generatesCredit}
                onCheckedChange={setGeneratesCredit}
                className="border-white/30 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
              />
              <div className="flex flex-col">
                <Label 
                  htmlFor="edit_generates_credit" 
                  className="text-white/90 cursor-pointer font-medium"
                >
                  Generates Credit
                </Label>
                <p className="text-xs text-white/50">
                  Mark this if selecting this result should mark the call as a credit (not payable)
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingOption(null);
                  setNewOptionName("");
                  setGeneratesCredit(false);
                }}
                className="text-white hover:bg-[#1a3a6a] border border-[#1a3a6a]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
              >
                {submitting ? "Updating..." : "Update Option"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Options Table */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="border-b border-[#1a3a6a] pb-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Chivo'] text-white flex items-center gap-2">
                <ListChecks size={18} className="text-[#f4cc1e]" />
                Current Disposition Values ({filteredAndSortedOptions().length})
              </CardTitle>
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery("")}
                  className="text-white/50 hover:text-white"
                >
                  Clear Filter
                </Button>
              )}
            </div>
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
              <Input
                placeholder="Search disposition values..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/40"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredAndSortedOptions().length === 0 ? (
            <div className="p-8 text-center text-white/50">
              <Settings size={48} className="mx-auto mb-4 opacity-50" />
              <p>{searchQuery ? "No matching disposition values found" : "No result options configured"}</p>
              <p className="text-sm mt-1">
                {searchQuery ? "Try a different search term" : "Add options that customers can select for their call results"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-[#1a3a6a]">
                      <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold w-8">
                        <GripVertical size={16} className="opacity-50" />
                      </TableHead>
                      <TableHead 
                        className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold cursor-pointer hover:bg-[#1a3a6a]"
                        onClick={() => handleSort("name")}
                      >
                        <div className="flex items-center gap-2">
                          Option Name
                          <ArrowUpDown size={14} className={sortKey === "name" ? "text-[#f4cc1e]" : "text-white/30"} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold cursor-pointer hover:bg-[#1a3a6a]"
                        onClick={() => handleSort("type")}
                      >
                        <div className="flex items-center gap-2">
                          Type
                          <ArrowUpDown size={14} className={sortKey === "type" ? "text-[#f4cc1e]" : "text-white/30"} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold cursor-pointer hover:bg-[#1a3a6a]"
                        onClick={() => handleSort("created")}
                      >
                        <div className="flex items-center gap-2">
                          Created
                          <ArrowUpDown size={14} className={sortKey === "created" ? "text-[#f4cc1e]" : "text-white/30"} />
                        </div>
                      </TableHead>
                      <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold text-right">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <SortableContext
                    items={filteredAndSortedOptions().map(opt => opt.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <TableBody>
                      {filteredAndSortedOptions().map((option) => (
                        <SortableRow
                          key={option.id}
                          option={option}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      ))}
                    </TableBody>
                  </SortableContext>
                </Table>
              </DndContext>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Settings size={20} className="text-[#f4cc1e] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-white/60">
              <p className="font-medium text-white/80 mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>These options appear in the "Results" dropdown on the Customer Dashboard</li>
                <li>Customers can select a result for each call in their Call History table</li>
                <li>Results are saved to the database (not Google Sheets)</li>
                <li>Deleting an option here will not affect previously saved results</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultOptions;
